import React, { useMemo } from 'react'
import Card from './ui/Card'
import SectionTitle from './ui/SectionTitle'

/** Map 0–1 (or open scale) energy to 0–100 bar height. */
function energyPct(v) {
  if (v == null || Number.isNaN(Number(v))) return null
  const n = Number(v)
  if (n <= 1.5) return Math.max(4, Math.min(100, n * 100))
  // already percent-ish
  return Math.max(4, Math.min(100, n))
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n))
}

/** Vertical meter: value from min→max, fill from bottom. */
function VerticalMeter({ label, value, min, max, unit, goodMax, warnMax, color = 'teal' }) {
  const pct = value == null || Number.isNaN(Number(value))
    ? 0
    : clamp(((Number(value) - min) / (max - min)) * 100, 0, 100)
  const tone =
    value == null
      ? 'bg-white/20'
      : warnMax != null && Number(value) > warnMax
        ? 'bg-rose-400'
        : goodMax != null && Number(value) > goodMax
          ? 'bg-amber-400'
              : color === 'amber'
            ? 'bg-amber-400'
            : color === 'emerald'
              ? 'bg-emerald-400'
              : color === 'teal'
                ? 'bg-[#00d4aa]'
                : 'bg-[#00d4aa]'

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative h-36 w-10 overflow-hidden rounded-lg border border-white/10 bg-black/40">
        <div
          className={`absolute bottom-0 left-0 right-0 transition-all duration-700 ease-out ${tone}`}
          style={{ height: `${pct}%`, opacity: 0.9 }}
        />
        {/* scale ticks */}
        <div className="pointer-events-none absolute inset-x-0 top-1/4 border-t border-white/10" />
        <div className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-white/15" />
        <div className="pointer-events-none absolute inset-x-0 top-3/4 border-t border-white/10" />
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold tabular-nums text-white">
          {value == null || Number.isNaN(Number(value)) ? '–' : Number(value).toFixed(1)}
          {unit ? <span className="ml-0.5 text-xs font-normal text-white/50">{unit}</span> : null}
        </p>
        <p className="mt-0.5 text-[11px] uppercase tracking-wide text-white/45">{label}</p>
      </div>
    </div>
  )
}

function EqBar({ label, pct, hint }) {
  const h = pct == null ? 8 : clamp(pct, 8, 100)
  return (
    <div className="flex flex-1 flex-col items-center gap-2">
      <div className="flex h-28 w-full items-end justify-center rounded-xl border border-white/10 bg-black/30 px-2 pb-2">
        <div
          className="w-full max-w-[3rem] rounded-md bg-gradient-to-t from-[#0a3d32] via-[#00d4aa]/85 to-[#99f6e4] transition-all duration-700"
          style={{ height: `${h}%` }}
          title={hint || label}
        />
      </div>
      <p className="text-xs font-medium text-white/80">{label}</p>
      <p className="text-[11px] tabular-nums text-white/45">{pct == null ? '–' : `${Math.round(pct)}%`}</p>
    </div>
  )
}

function StageChip({ title, active, detail }) {
  return (
    <div
      className={`rounded-xl border px-3 py-2.5 ${
        active
          ? 'border-[#00d4aa]/35 bg-[#00d4aa]/10 shadow-[0_0_24px_-12px_rgba(0,212,170,0.55)]'
          : 'border-white/10 bg-white/[0.03] opacity-60'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${active ? 'bg-emerald-400' : 'bg-white/25'}`} />
        <p className="text-sm font-medium text-white">{title}</p>
      </div>
      {detail ? <p className="mt-1 text-[11px] leading-snug text-white/55">{detail}</p> : null}
    </div>
  )
}

function fmtDb(v, digits = 1) {
  if (v == null || Number.isNaN(Number(v))) return null
  const n = Number(v)
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(digits)} dB`
}

/**
 * Studio-Analyse: LUFS/Peak-Meter, EQ-Balance, Processing-Chain.
 * Funktioniert mit processing_json (neue Masters) und Fallback aus analysis/render/plan.
 */
export default function StudioAnalysis({ track, render, plan }) {
  const analysis = track?.analysis || null
  const processing = render?.processing || null

  const data = useMemo(() => {
    const eq = processing?.eq_balance || {}
    const loud = processing?.loudness || {}
    const peak = processing?.peak || {}
    const stages = processing?.stages || {}

    const originalLufs = loud.original_lufs ?? analysis?.lufs_integrated ?? null
    const finalLufs = loud.final_lufs ?? render?.qc_lufs ?? null
    const targetLufs = loud.target_lufs ?? plan?.loudness_target_lufs ?? null
    const originalTp = peak.original_tp ?? analysis?.true_peak_db ?? null
    const finalTp = peak.final_tp ?? render?.qc_true_peak ?? null
    const ceiling = peak.ceiling_db ?? plan?.limiter_ceiling_db ?? -1

    const bass = energyPct(eq.bass ?? analysis?.low_energy_20_120 ?? analysis?.energy_low)
    const lowMid = energyPct(eq.low_mid ?? analysis?.lowmid_energy_150_350)
    const presence = energyPct(eq.presence ?? analysis?.presence_energy_1k_3k ?? analysis?.energy_mid)
    const air = energyPct(eq.air ?? analysis?.air_energy_8k_14k ?? analysis?.energy_high)

    const cleanup = stages.cleanup || {}
    const musical = stages.musical || {}
    const harmony = musical.harmony || null
    const tonal = musical.tonal_balance || null
    const dynamics = stages.dynamics || {}
    const lim = stages.limiter || {}
    const le = stages.loudness_engine || null

    const cleanupActive = cleanup.enabled !== false && (cleanup.enabled || track?.ai_cleanup_enabled)
    const musicalActive = !!(tonal || musical.low_end || musical.multiband_gr || musical.saturation || musical.stereo)
    const harmonyActive = !!(harmony && (harmony.mid_pct || harmony.low_pct || harmony.high_pct))
    const dynamicsActive = !!(dynamics.applied || (dynamics.actions && dynamics.actions.length))
    const loudnessActive = finalLufs != null
    const limiterActive = finalTp != null || lim.used

    const tonalDetail = tonal
      ? `Low ${fmtDb(tonal.low_gain_db)} · LowMid ${fmtDb(tonal.lowmid_gain_db)} · Presence ${fmtDb(tonal.presence_gain_db)} · Air ${fmtDb(tonal.air_gain_db)}`
      : le?.tonal_moves?.length
        ? `Tone: ${Array.isArray(le.tonal_moves) ? le.tonal_moves.slice(0, 3).join(', ') : 'angepasst'}`
        : cleanupActive
          ? `Stärke ${cleanup.strength || track?.ai_cleanup_strength || 'medium'}`
          : 'Übersprungen / minimal'

    return {
      originalLufs,
      finalLufs,
      targetLufs,
      originalTp,
      finalTp,
      ceiling,
      bass,
      lowMid,
      presence,
      air,
      lra: loud.lra_final ?? null,
      crest: loud.crest_final ?? analysis?.crest_factor ?? null,
      stages: [
        {
          title: 'AI Cleanup',
          active: !!cleanupActive,
          detail: cleanupActive
            ? `Stärke ${cleanup.strength || track?.ai_cleanup_strength || '–'}${cleanup.treble_db != null ? ` · Treble ${fmtDb(cleanup.treble_db)}` : ''}`
            : 'Aus',
        },
        {
          title: 'EQ / Tonal',
          active: musicalActive || !!(le && le.tonal_moves?.length),
          detail: tonalDetail,
        },
        {
          title: 'Harmony',
          active: harmonyActive,
          detail: harmonyActive
            ? `Low ${harmony.low_pct}% · Mid ${harmony.mid_pct}% · High ${harmony.high_pct}%`
            : 'Aus / Dose-Guard',
        },
        {
          title: 'Dynamics',
          active: dynamicsActive,
          detail: dynamicsActive
            ? (dynamics.actions || []).join(', ') || 'angepasst'
            : 'Nicht nötig',
        },
        {
          title: 'Loudness',
          active: loudnessActive,
          detail:
            targetLufs != null && finalLufs != null
              ? `Ziel ${Number(targetLufs).toFixed(1)} → ${Number(finalLufs).toFixed(1)} LUFS`
              : finalLufs != null
                ? `${Number(finalLufs).toFixed(1)} LUFS`
                : '–',
        },
        {
          title: 'Limiter',
          active: limiterActive,
          detail:
            finalTp != null
              ? `TP ${Number(finalTp).toFixed(2)} dB (Ceiling ${Number(ceiling).toFixed(1)})${lim.gr_estimate_db != null ? ` · GR ~${Number(lim.gr_estimate_db).toFixed(1)} dB` : ''}`
              : `Ceiling ${Number(ceiling).toFixed(1)} dB`,
        },
      ],
      hasProcessingDetail: !!processing,
      doseGuard: !!processing?.dose_guard,
      profile: processing?.profile || plan?.target_profile,
    }
  }, [analysis, processing, render, plan, track])

  if (!analysis && !render) return null

  return (
    <Card className="overflow-hidden">
      <div className="mb-1 flex flex-wrap items-end justify-between gap-3">
        <div>
          <SectionTitle className="mb-1">Studio-Analyse</SectionTitle>
          <p className="text-sm text-white/55">
            Lautheit, Peak und EQ-Balance – plus die angewandte Processing-Chain dieses Masters.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          {data.profile ? (
            <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-white/70">
              Profil: {data.profile}
            </span>
          ) : null}
          {data.doseGuard ? (
            <span className="rounded-full border border-amber-400/30 bg-amber-500/10 px-2.5 py-1 text-amber-200/90">
              Dose-Guard aktiv
            </span>
          ) : null}
          {!data.hasProcessingDetail && hasMasterHint(render) ? (
            <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-white/50">
              Basis-Analyse · Chain nach Remaster vollständig
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[auto_1fr]">
        {/* Meters */}
        <div className="flex flex-wrap justify-center gap-6 sm:justify-start">
          <VerticalMeter
            label="Peak Original"
            value={data.originalTp}
            min={-20}
            max={3}
            unit="dB"
            goodMax={-0.1}
            warnMax={0}
            color="amber"
          />
          <VerticalMeter
            label="Peak Master"
            value={data.finalTp}
            min={-20}
            max={3}
            unit="dB"
            goodMax={data.ceiling}
            warnMax={data.ceiling + 0.3}
            color="emerald"
          />
          <VerticalMeter
            label="LUFS Original"
            value={data.originalLufs}
            min={-30}
            max={-6}
            unit="LUFS"
            color="amber"
          />
          <VerticalMeter
            label="LUFS Master"
            value={data.finalLufs}
            min={-30}
            max={-6}
            unit="LUFS"
            color="teal"
          />
        </div>

        {/* EQ Balance */}
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-white/45">EQ Balance</p>
          <div className="flex gap-3 sm:gap-4">
            <EqBar label="Bass" pct={data.bass} hint="20–120 Hz" />
            <EqBar label="Low Mid" pct={data.lowMid} hint="150–350 Hz" />
            <EqBar label="Presence" pct={data.presence} hint="1–3 kHz" />
            <EqBar label="Air" pct={data.air} hint="8–14 kHz" />
          </div>
          {(data.targetLufs != null || data.lra != null || data.crest != null) && (
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-white/55">
              {data.targetLufs != null && (
                <span>
                  Ziel: <strong className="text-white/85">{Number(data.targetLufs).toFixed(1)} LUFS</strong>
                </span>
              )}
              {data.lra != null && (
                <span>
                  LRA: <strong className="text-white/85">{Number(data.lra).toFixed(1)}</strong>
                </span>
              )}
              {data.crest != null && (
                <span>
                  Crest: <strong className="text-white/85">{Number(data.crest).toFixed(1)} dB</strong>
                </span>
              )}
              {data.ceiling != null && (
                <span>
                  Ceiling: <strong className="text-white/85">{Number(data.ceiling).toFixed(1)} dBTP</strong>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Processing chain */}
      <div className="mt-8">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-white/45">Processing Chain</p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {data.stages.map((s) => (
            <StageChip key={s.title} title={s.title} active={s.active} detail={s.detail} />
          ))}
        </div>
      </div>
    </Card>
  )
}

function hasMasterHint(render) {
  return !!(render && (render.qc_lufs != null || render.qc_true_peak != null))
}
